/**
 * Strict parser for the read-only capability matrix (`capabilities.v1`).
 *
 * Everything that reaches the UI is parsed here first: unknown keys, missing keys,
 * an entry stored under the wrong id, or an unexpected timestamp all fail closed, so
 * the matrix shows either verified facts or nothing at all.
 */

import { z } from "zod";

export const capabilityIds = ["coordinator", "explee", "leadocean", "chooseflow", "zoho_learn", "sheets", "bigin", "zia"] as const;

const cellSchema = z.object({ value: z.boolean(), source: z.string().min(1).max(60), observed_at: z.string().datetime({ offset: true }).nullable() }).strict();

const entrySchema = z.object({ id: z.enum(capabilityIds), name: z.string().min(1).max(60), implemented: z.boolean(), configured: cellSchema, granted: cellSchema, connected: cellSchema, healthy: cellSchema, tool_allowed: cellSchema, tools: z.array(z.string().min(1).max(80)).max(10) }).strict();

export const capabilitiesSchema = z.object({ schema_version: z.literal("capabilities.v1"), generated_at: z.string().datetime({ offset: true }), capabilities: z.object({ coordinator: entrySchema, explee: entrySchema, leadocean: entrySchema, chooseflow: entrySchema, zoho_learn: entrySchema, sheets: entrySchema, bigin: entrySchema, zia: entrySchema }).strict() }).strict().superRefine((matrix, ctx) => {
  for (const id of capabilityIds) {
    const entry = matrix.capabilities[id];
    if (entry.id !== id) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["capabilities", id, "id"], message: "capability id does not match its key" });
  }
});

export type Capabilities = z.infer<typeof capabilitiesSchema>;

export function parseCapabilities(value: unknown): Capabilities {
  return capabilitiesSchema.parse(value);
}
